<script lang="ts" setup>
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { useEventListener } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDeactivated } from '@/composables/vue'

export interface Props {
  /**
   * level of depth
   *
   * @default 100
   */
  zIndex?: number

  /**
   * whether to allow close dialog by clicking mask layer
   *
   * @default true
   */
  closeByMask?: boolean

  /**
   * use v-if, destroy all the internal elements after closed
   *
   * @default true
   */
  useVIf?: boolean

  /**
   * keep the dialog opened even when in other views
   *
   * @default false
   */
  keepAlive?: boolean

  /**
   * The aria-labelledby id for the dialog.
   */
  dialogLabelledBy?: string

  slow?: boolean

  halfTrans: boolean
}

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(defineProps<Props>(), {
  zIndex: 1001,
  closeByMask: true,
  useVIf: true,
  keepAlive: false,
  slow: false,
  halfTrans: false
})

const emit = defineEmits<{
  /** v-model dialog visibility */
  (event: 'close'): void
}>()

const visible = defineModel<boolean>({ required: true })

const deactivated = useDeactivated()
const route = useRoute()

/** scrollable HTML element */
const elDialogMain = ref<HTMLDivElement>()
const elDialogRoot = ref<HTMLDivElement>()

const { activate } = useFocusTrap(elDialogRoot, {
  immediate: false,
  allowOutsideClick: true,
  clickOutsideDeactivates: true,
  escapeDeactivates: true,
  preventScroll: true,
  returnFocusOnDeactivate: true
})

defineExpose({
  elDialogRoot,
  elDialogMain
})

/** close the dialog */
function close() {
  if (!visible.value) return
  visible.value = false
  emit('close')
}

function clickMask() {
  if (props.closeByMask) close()
}

const routePath = ref(route.path)
watch(visible, (value) => {
  if (value) routePath.value = route.path
})

const halfTrans = computed(() => {
  return props.halfTrans
})

const notInCurrentPage = computed(() => deactivated.value || routePath.value !== route.path)
watch(notInCurrentPage, (value) => {
  if (props.keepAlive) return
  if (value) close()
})

// controls the state of v-if.
// when useVIf is toggled, v-if has the same state as modelValue, otherwise v-if is true
const isVIf = computed(() => {
  return props.useVIf ? visible.value : true
})

// controls the state of v-show.
// when useVIf is toggled, v-show is true, otherwise it has the same state as modelValue
const isVShow = computed(() => {
  return !props.useVIf ? visible.value : true
})

function bindTypeToAny($attrs: any) {
  return $attrs as any
}

function trapFocusDialog() {
  if (isVShow.value) nextTick().then(() => activate())
}

useEventListener('keydown', (e: KeyboardEvent) => {
  if (!visible.value) return
  if (e.key === 'Escape') {
    close()
    e.preventDefault()
  }
})

const dontOptimize = true // !getPreferences(userSettings, 'optimizeForLowPerformanceDevice')
</script>

<template>
  <!-- https://cn.vuejs.org/guide/built-ins/teleport 模态框 -->
  <Teleport to="body">
    <!-- Dialog component -->
    <!-- @transitionend="trapFocusDialog" -->
    <Transition :name="slow ? 'dialogslow-visible' : 'dialog-visible'">
      <div
        v-if="isVIf"
        v-show="isVShow"
        ref="elDialogRoot"
        aria-modal="true"
        :aria-labelledby="dialogLabelledBy"
        :style="{
          'z-index': zIndex
        }"
        class="no-scrollbar"
        fixed
        inset-0
        of-y-auto
        overscroll-none>
        <!-- The style `scrollbar-hide overscroll-none overflow-y-scroll` and `h="[calc(100%+0.5px)]"` is used to implement scroll locking, -->
        <!-- corresponding to issue: #106, so please don't remove it. -->
        <!-- Mask layer: blur -->
        <div
          v-if="!halfTrans"
          class="dialog-mask"
          :class="{
            'backdrop-blur-sm': dontOptimize
          }"
          absolute
          inset-0
          z-0
          bg-dark
          touch-none
          opacity-40
          backdrop-filter />
        <!-- Mask layer: dimming -->
        <div
          class="dialog-mask"
          absolute
          inset-0
          z-0
          touch-none
          bg-black
          opacity-60
          h="[calc(100%+0.5px)]"
          @click="clickMask" />
        <!-- Dialog container -->
        <div class="p-safe-area" pointer-events-none absolute inset-0 z-1 flex opacity-100>
          <div flex flex-1 items-center justify-center>
            <!-- We use `class` here to make v-bind being able to be override them -->
            <div
              ref="elDialogMain"
              class="dialog-main pointer-events-auto isolate max-h-full w-full touch-pan-x touch-pan-y of-y-auto overscroll-contain border-1px border-base rounded border-solid bg-base shadow-lg"
              v-bind="bindTypeToAny($attrs)">
              <slot />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.dialog-visible-enter-active,
.dialog-visible-leave-active {
  transition-duration: 0.25s;

  .dialog-mask {
    transition: opacity 0.25s ease;
  }

  .dialog-main {
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
  }
}

.dialogslow-visible-enter-active,
.dialogslow-visible-leave-active {
  transition-duration: 0.5s;

  .dialog-mask {
    transition: opacity 0.5s ease;
  }

  .dialog-main {
    transition:
      opacity 0.5s ease,
      transform 0.5s ease;
  }
}

.dialog-visible-enter-from,
.dialog-visible-leave-to {
  .dialog-mask {
    opacity: 0;
  }

  .dialog-main {
    transform: translateY(50px);
    opacity: 0;
  }
}

.dialogslow-visible-enter-from,
.dialogslow-visible-leave-to {
  .dialog-mask {
    opacity: 0;
  }

  .dialog-main {
    transform: translateY(50px);
    opacity: 0;
  }
}

.p-safe-area {
  padding-top: env(safe-area-inset-top);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
}
</style>
